import { SLOT_SYMBOL_IMAGES } from "../../../../../static/Constants";
import { colors, borderRadiusPx, customShadows, THREESLOT_COLOR_THEME } from "../../../../../theme";

/**
 * 3-Slot Detail Page Theme & Layout Config (Centralized Tokens)
 */
export const THREE_SLOT_DETAILS_THEME = {
  pageBg: colors.dashboard.canvas,
  cardMaxWidth: 1040,
  containerMaxWidth: "lg",
  headerBorderRadius: borderRadiusPx.xl,
  machineBorderRadius: borderRadiusPx.xxl,
  shadow: customShadows.card,
  hoverShadow: customShadows.cardHover,
  colorTheme: THREESLOT_COLOR_THEME,
};

/**
 * Centralized Progressive Jackpot Tier Themes (Mega, Major, Mini)
 * Styled with light, clean surfaces and vibrant lottery accent borders & text
 */
export const JACKPOT_TIER_THEMES = {
  mega: {
    id: "mega",
    label: "👑 MEGA JACKPOT",
    background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
    border: "1.5px solid #fca5a5",
    borderColor: "#fca5a5",
    boxShadow: "0 2px 8px rgba(239, 68, 68, 0.12)",
    titleColor: "#dc2626",
    valueColor: "#991b1b",
    borderRadius: borderRadiusPx.md || "10px",
  },
  major: {
    id: "major",
    label: "💎 MAJOR JACKPOT",
    background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
    border: "1.5px solid #d8b4fe",
    borderColor: "#d8b4fe",
    boxShadow: "0 2px 8px rgba(168, 85, 247, 0.12)",
    titleColor: "#7c3aed",
    valueColor: "#581c87",
    borderRadius: borderRadiusPx.md || "10px",
  },
  mini: {
    id: "mini",
    label: "🔔 MINI JACKPOT",
    background: "linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)",
    border: "1.5px solid #fde047",
    borderColor: "#fde047",
    boxShadow: "0 2px 8px rgba(234, 179, 8, 0.12)",
    titleColor: "#ca8a04",
    valueColor: "#854d0e",
    borderRadius: borderRadiusPx.md || "10px",
  },
};

/**
 * Side Badge Numbers with Casino Neon Colors
 */
export const SIDE_BADGES = [
  { num: "4", color: colors.red[500] || "#ef4444" },
  { num: "2", color: colors.blue[500] || "#3b82f6" },
  { num: "9", color: colors.emerald[500] || "#22c55e" },
  { num: "6", color: colors.amber[500] || "#eab308" },
  { num: "1", color: colors.purple[500] || "#a855f7" },
  { num: "7", color: "#f97316" },
  { num: "8", color: colors.cyan[500] || "#06b6d4" },
  { num: "3", color: "#ec4899" },
  { num: "5", color: colors.blue[500] || "#3b82f6" },
];

/**
 * 3-Slot Fixed Payline Patterns (Coordinates & SVG Geometries)
 * Coordinate system: [reelIndex (0..2), rowIndex (0..2)]
 * Row 0: Top row (y=54), Row 1: Middle row (y=150), Row 2: Bottom row (y=246)
 *
 * NOTE: Patterns and coordinates are strictly FIXED.
 * Multipliers and the number of active selectable lines scale dynamically from contract or game data.
 */
export const FIXED_THREE_SLOT_PAYLINE_PATTERNS = [
  {
    id: 1,
    lineNum: 1,
    name: "Line 1",
    shortLabel: "L1",
    pattern: "Horizontal Middle",
    where: "Middle row straight across",
    defaultMultiplier: 1,
    color: "#a855f7", // Vivid Purple
    badgeColor: "#8b5cf6",
    bgAlpha: "rgba(168, 85, 247, 0.15)",
    borderGlow: "0 0 10px rgba(168, 85, 247, 0.8)",
    coords: [
      [0, 1],
      [1, 1],
      [2, 1],
    ],
    svgPath: "M 0 150 L 300 150",
  },
  {
    id: 2,
    lineNum: 2,
    name: "Line 2",
    shortLabel: "L2",
    pattern: "Horizontal Top",
    where: "Top row straight across",
    defaultMultiplier: 2,
    color: "#3b82f6", // Royal Blue
    badgeColor: "#2563eb",
    bgAlpha: "rgba(59, 130, 246, 0.15)",
    borderGlow: "0 0 10px rgba(59, 130, 246, 0.8)",
    coords: [
      [0, 0],
      [1, 0],
      [2, 0],
    ],
    svgPath: "M 0 54 L 300 54",
  },
  {
    id: 3,
    lineNum: 3,
    name: "Line 3",
    shortLabel: "L3",
    pattern: "Horizontal Bottom",
    where: "Bottom row straight across",
    defaultMultiplier: 3,
    color: "#10b981", // Emerald Green
    badgeColor: "#059669",
    bgAlpha: "rgba(16, 185, 129, 0.15)",
    borderGlow: "0 0 10px rgba(16, 185, 129, 0.8)",
    coords: [
      [0, 2],
      [1, 2],
      [2, 2],
    ],
    svgPath: "M 0 246 L 300 246",
  },
  {
    id: 4,
    lineNum: 4,
    name: "Line 4",
    shortLabel: "L4",
    pattern: "Diagonal Down",
    where: "Top-Left -> Center -> Bottom-Right",
    defaultMultiplier: 5,
    color: "#f97316", // Neon Orange / Amber
    badgeColor: "#ea580c",
    bgAlpha: "rgba(249, 115, 22, 0.15)",
    borderGlow: "0 0 10px rgba(249, 115, 22, 0.8)",
    coords: [
      [0, 0],
      [1, 1],
      [2, 2],
    ],
    svgPath: "M 5 20 L 50 54 L 150 150 L 250 246 L 295 280",
  },
  {
    id: 5,
    lineNum: 5,
    name: "Line 5",
    shortLabel: "L5",
    pattern: "Diagonal Up",
    where: "Bottom-Left -> Center -> Top-Right",
    defaultMultiplier: 10,
    color: "#eab308", // Sun Gold
    badgeColor: "#ca8a04",
    bgAlpha: "rgba(234, 179, 8, 0.15)",
    borderGlow: "0 0 12px rgba(234, 179, 8, 0.9)",
    coords: [
      [0, 2],
      [1, 1],
      [2, 0],
    ],
    svgPath: "M 5 280 L 50 246 L 150 150 L 250 54 L 295 20",
  },
  // Scalable fixed patterns 6..9 for future contract expansion
  {
    id: 6,
    lineNum: 6,
    name: "Line 6",
    shortLabel: "L6",
    pattern: "V-Shape",
    where: "Top-Left -> Center-Bottom -> Top-Right",
    defaultMultiplier: 12,
    color: "#ec4899", // Pink
    badgeColor: "#db2777",
    bgAlpha: "rgba(236, 72, 153, 0.15)",
    borderGlow: "0 0 10px rgba(236, 72, 153, 0.8)",
    coords: [
      [0, 0],
      [1, 2],
      [2, 0],
    ],
    svgPath: "M 5 54 L 150 246 L 295 54",
  },
  {
    id: 7,
    lineNum: 7,
    name: "Line 7",
    shortLabel: "L7",
    pattern: "Inverted V",
    where: "Bottom-Left -> Center-Top -> Bottom-Right",
    defaultMultiplier: 15,
    color: "#06b6d4", // Cyan
    badgeColor: "#0891b2",
    bgAlpha: "rgba(6, 182, 212, 0.15)",
    borderGlow: "0 0 10px rgba(6, 182, 212, 0.8)",
    coords: [
      [0, 2],
      [1, 0],
      [2, 2],
    ],
    svgPath: "M 5 246 L 150 54 L 295 246",
  },
  {
    id: 8,
    lineNum: 8,
    name: "Line 8",
    shortLabel: "L8",
    pattern: "Top Step",
    where: "Top-Left -> Middle-Center -> Bottom-Right",
    defaultMultiplier: 20,
    color: "#84cc16", // Lime
    badgeColor: "#65a30d",
    bgAlpha: "rgba(132, 204, 22, 0.15)",
    borderGlow: "0 0 10px rgba(132, 204, 22, 0.8)",
    coords: [
      [0, 0],
      [1, 1],
      [2, 2],
    ],
    svgPath: "M 5 54 L 150 150 L 295 246",
  },
  {
    id: 9,
    lineNum: 9,
    name: "Line 9",
    shortLabel: "L9",
    pattern: "Bottom Step",
    where: "Bottom-Left -> Middle-Center -> Top-Right",
    defaultMultiplier: 25,
    color: "#6366f1", // Indigo
    badgeColor: "#4f46e5",
    bgAlpha: "rgba(99, 102, 241, 0.15)",
    borderGlow: "0 0 10px rgba(99, 102, 241, 0.8)",
    coords: [
      [0, 2],
      [1, 1],
      [2, 0],
    ],
    svgPath: "M 5 246 L 150 150 L 295 54",
  },
];

export const DEFAULT_LINE_MULTIPLIERS = [1, 2, 3, 5, 10];
export const DEFAULT_TOTAL_LINES = 5;

/**
 * Factory to build 3-Slot paylines dynamically with contract-driven multipliers
 * and scalable total lines count, while keeping the geometric patterns strictly fixed.
 *
 * @param {Array<number>|null} multipliers Custom line multipliers (from contract/game data)
 * @param {number|null} totalLines Total number of selectable lines available
 * @returns {Array<Object>} List of hydrated payline definitions
 */
export const getThreeSlotPaylines = (multipliers = null, totalLines = null) => {
  const lineCount = totalLines
    ? Math.max(1, Math.min(Number(totalLines), FIXED_THREE_SLOT_PAYLINE_PATTERNS.length))
    : Array.isArray(multipliers) && multipliers.length > 0
    ? Math.min(multipliers.length, FIXED_THREE_SLOT_PAYLINE_PATTERNS.length)
    : DEFAULT_TOTAL_LINES;

  return FIXED_THREE_SLOT_PAYLINE_PATTERNS.slice(0, lineCount).map((pattern, idx) => {
    const customMultiplier =
      Array.isArray(multipliers) && multipliers[idx] !== undefined && Number(multipliers[idx]) > 0
        ? Number(multipliers[idx])
        : pattern.defaultMultiplier;

    return {
      ...pattern,
      multiplier: customMultiplier,
      label: `${pattern.name} (${customMultiplier}x)`,
      shortLabel: `L${pattern.lineNum}`,
    };
  });
};

/**
 * Dedicated 3-Slot Paylines (Default fallback)
 */
export const THREE_SLOT_PAYLINES = getThreeSlotPaylines(DEFAULT_LINE_MULTIPLIERS, DEFAULT_TOTAL_LINES);

/**
 * Slot Machine Arcade Frame Theme Tokens (Consistent with Platform Card Design)
 */
export const SLOT_MACHINE_FRAME_THEME = {
  bg: "#ffffff",
  headerBg: "#f8fafc",
  border: "1px solid #e2e8f0",
  boxShadow: customShadows.cardFlat || "0 4px 20px rgba(0, 0, 0, 0.05)",
  borderRadius: { xs: borderRadiusPx.lg || "16px", sm: borderRadiusPx.xl || "20px" },
  headerBorderRadius: borderRadiusPx.md || "8px",
  goldBorder: "#eab308",
  goldText: "#b45309",
  amberAccent: "#f59e0b",
};

/**
 * 3-SlotBET Game Configuration
 * Fiat & Crypto 5-Symbol Editions (Contract Mapped 0..4)
 */
export const THREE_SLOT_CONFIG = {
  name: "3-SlotBET",
  subtitle: "PROGRESSIVE SLOT",
  reels: 3,
  rows: 3,
  paylines: 1,
  paylineDescription: "Match 3 symbols on the center payline to win!",
  initialReels: ["bell", "diamond", "crown"],
  defaultBet: 5.0,
  minBet: 1.0,
  maxBet: 500.0,
  betSteps: [1, 2, 5, 10, 25, 50, 100, 250, 500],
  currencySymbol: "BET",
  currencyName: "BET",
};

export const JACKPOT_DEFAULTS = {
  mega: 50000.0,
  major: 10000.0,
  mini: 1000.0,
};

export const THEME_OPTIONS = [
  {
    id: "fiat",
    label: "High-Roller Vegas Slot",
    description: "Authentic casino symbols: Golden Bell (Mini), Brilliant Diamond (Major), Royal Crown (Mega), Casino Cherries (5 Spins), Lucky 7 (10 Super Spins)",
  },
  {
    id: "crypto",
    label: "Crypto Slot",
    description: "Authentic 3D crypto coins: Polygon POL (Mini), Ethereum ETH (Major), Bitcoin BTC (Mega), Solana SOL (5 Spins), Tether USDT (10 Super Spins)",
  },
];

/**
 * Authentic Casino Gambling 5-Symbol Set (Contract Mapped 0 to 4)
 * 0: Mini Progressive Jackpot (10x) -> Golden Liberty Bell
 * 1: 5 Free Spins (15x) -> Casino Cherries
 * 2: 10 Super Free Spins (25x) -> Lucky 7
 * 3: Major Progressive Jackpot (30x) -> Brilliant Diamond
 * 4: Mega Progressive Jackpot (50x) -> Royal Crown
 */
export const FIAT_SET_SYMBOLS = [
  {
    contractNumber: 0,
    id: "bell",
    label: "Golden Bell",
    winningTier: "Mini Jackpot",
    winOutcome: "Mini Progressive Jackpot",
    code: "BELL",
    symbolText: "🔔",
    iconText: "🔔",
    multiplier: 10,
    tier: "Mini Jackpot",
    tierColor: "#ca8a04",
    color: "#ca8a04",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.bell,
    payout: "Mini Jackpot (10x Base + Mini Pool)",
    description: "Match 3 Golden Bells to win Mini Progressive Jackpot & 10x payout",
  },
  {
    contractNumber: 1,
    id: "cherries",
    label: "Casino Cherries",
    winningTier: "5 Free Spins",
    winOutcome: "5 Free Spins",
    code: "CHERRY",
    symbolText: "🍒",
    iconText: "🍒",
    multiplier: 15,
    tier: "5 Free Spins",
    tierColor: "#ef4444",
    color: "#dc2626",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.cherries,
    payout: "5 Free Spins (15x Base)",
    description: "Match 3 Casino Cherries to trigger 5 Free Spins Bonus Round",
  },
  {
    contractNumber: 2,
    id: "lucky_7",
    label: "Lucky 7",
    winningTier: "10 Super Spins",
    winOutcome: "10 Super Free Spins",
    code: "SEVEN",
    symbolText: "7️⃣",
    iconText: "7️⃣",
    multiplier: 25,
    tier: "10 Super Spins",
    tierColor: "#dc2626",
    color: "#b91c1c",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.lucky_7,
    payout: "10 Super Spins (25x Base)",
    description: "Match 3 Lucky 7s for 10 Super Free Spins Bonus Round",
  },
  {
    contractNumber: 3,
    id: "diamond",
    label: "Brilliant Diamond",
    winningTier: "Major Jackpot",
    winOutcome: "Major Progressive Jackpot",
    code: "DIAMOND",
    symbolText: "💎",
    iconText: "💎",
    multiplier: 30,
    tier: "Major Jackpot",
    tierColor: "#3b82f6",
    color: "#2563eb",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.diamond,
    payout: "Major Jackpot (30x Base + Major Pool)",
    description: "Match 3 Brilliant Diamonds to win Major Progressive Jackpot & 30x payout",
  },
  {
    contractNumber: 4,
    id: "crown",
    label: "Royal Crown",
    winningTier: "Mega Jackpot",
    winOutcome: "Mega Progressive Jackpot",
    code: "CROWN",
    symbolText: "👑",
    iconText: "👑",
    multiplier: 50,
    tier: "Mega Jackpot",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "fiat",
    category: "fiat",
    image: SLOT_SYMBOL_IMAGES.crown,
    payout: "Mega Jackpot (50x Base + Mega Pool)",
    description: "Match 3 Royal Crowns to win Grand Mega Progressive Jackpot & 50x payout",
  },
];

/**
 * CRYPTO Slot 5-Symbol Set (Contract Mapped 0 to 4)
 * 0: Mini Progressive Jackpot (10x) -> Polygon POL
 * 1: 5 Free Spins (15x) -> Solana SOL
 * 2: 10 Super Free Spins (25x) -> Tether USDT
 * 3: Major Progressive Jackpot (30x) -> Ethereum ETH
 * 4: Mega Progressive Jackpot (50x) -> Bitcoin BTC
 */
export const CRYPTO_SET_SYMBOLS = [
  {
    contractNumber: 0,
    id: "pol",
    label: "Polygon POL",
    winningTier: "Mini Jackpot",
    winOutcome: "Mini Progressive Jackpot",
    code: "POL",
    symbolText: "🟣",
    iconText: "🟣",
    multiplier: 10,
    tier: "Mini Jackpot",
    tierColor: "#a855f7",
    color: "#9333ea",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.pol,
    payout: "Mini Jackpot (10x Base + Mini Pool)",
    description: "Match 3 POL to win Mini Progressive Jackpot & 10x payout",
  },
  {
    contractNumber: 1,
    id: "sol",
    label: "Solana SOL",
    winningTier: "5 Free Spins",
    winOutcome: "5 Free Spins",
    code: "SOL",
    symbolText: "🟣",
    iconText: "🟣",
    multiplier: 15,
    tier: "5 Free Spins",
    tierColor: "#06b6d4",
    color: "#0891b2",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.sol,
    payout: "5 Free Spins (15x Base)",
    description: "Match 3 Solana coins to trigger 5 Free Spins Web3 Bonus Round",
  },
  {
    contractNumber: 2,
    id: "usdt",
    label: "Tether USDT",
    winningTier: "10 Super Spins",
    winOutcome: "10 Super Free Spins",
    code: "USDT",
    symbolText: "🟢",
    iconText: "🟢",
    multiplier: 25,
    tier: "Super Spins",
    tierColor: "#10b981",
    color: "#059669",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.usdt,
    payout: "10 Super Spins (25x Base)",
    description: "Match 3 Tether USDT coins for 10 Super Free Spins Bonus Round",
  },
  {
    contractNumber: 3,
    id: "eth",
    label: "Ethereum ETH",
    winningTier: "Major Jackpot",
    winOutcome: "Major Progressive Jackpot",
    code: "ETH",
    symbolText: "🔷",
    iconText: "🔷",
    multiplier: 30,
    tier: "Major Jackpot",
    tierColor: "#3b82f6",
    color: "#2563eb",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.eth,
    payout: "Major Jackpot (30x Base + Major Pool)",
    description: "Match 3 Ethereum coins to win Major Progressive Jackpot & 30x payout",
  },
  {
    contractNumber: 4,
    id: "btc",
    label: "Bitcoin BTC",
    winningTier: "Mega Jackpot",
    winOutcome: "Mega Progressive Jackpot",
    code: "BTC",
    symbolText: "₿",
    iconText: "₿",
    multiplier: 50,
    tier: "Mega Jackpot",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "crypto",
    category: "crypto",
    image: SLOT_SYMBOL_IMAGES.btc,
    payout: "Mega Jackpot (50x Base + Mega Pool)",
    description: "Match 3 Bitcoins to win Grand Mega Progressive Jackpot & 50x payout",
  },
];

export const ALL_SYMBOLS = [...FIAT_SET_SYMBOLS, ...CRYPTO_SET_SYMBOLS];
export const SYMBOLS = ALL_SYMBOLS;

/**
 * Helper to get the 5 symbols mapped from 0 to 4 based on Fiat or Crypto category
 */
export const getSymbolsByCategory = (category = "fiat") => {
  if (category === "crypto") {
    return CRYPTO_SET_SYMBOLS;
  }
  return FIAT_SET_SYMBOLS;
};

export const getSymbolsByTheme = (themeName = "fiat") => {
  return getSymbolsByCategory(themeName);
};

export const getRandomSymbol = (category = "fiat") => {
  const symbolSet = getSymbolsByCategory(category);
  const randomIndex = Math.floor(Math.random() * symbolSet.length);
  return symbolSet[randomIndex] || symbolSet[0];
};

export const getSymbolById = (id) => {
  return ALL_SYMBOLS.find((symbol) => symbol.id === id) || FIAT_SET_SYMBOLS[0];
};

/**
 * Helper to get symbol by contract index (0 to 4)
 * 0: Golden Bell / Polygon POL (Mini Jackpot, 10x)
 * 1: Casino Cherries / Solana SOL (5 Free Spins, 15x)
 * 2: Lucky 7 / Tether USDT (10 Super Free Spins, 25x)
 * 3: Brilliant Diamond / Ethereum ETH (Major Jackpot, 30x)
 * 4: Royal Crown / Bitcoin BTC (Mega Jackpot, 50x)
 */
export const getSymbolByContractNumber = (num, category = "fiat") => {
  const symbolSet = getSymbolsByCategory(category);
  const parsed = parseInt(num, 10);
  if (isNaN(parsed)) return symbolSet[0];
  const safeIdx = Math.abs(parsed) % symbolSet.length;
  return symbolSet[safeIdx];
};

/**
 * Maps 9 icon places (values 0..4) returned from the contract or fair RNG into the 3x3 reel grid.
 * Grid structure: reelGrid[reelIndex][rowIndex] where reelIndex = 0..2 and rowIndex = 0..2.
 *
 * Pattern and coordinate alignment:
 * Row 0 (Top row): [0,0], [1,0], [2,0] -> Payline 2 (Horizontal Top, 2x)
 * Row 1 (Middle row): [0,1], [1,1], [2,1] -> Payline 1 (Horizontal Middle, 1x)
 * Row 2 (Bottom row): [0,2], [1,2], [2,2] -> Payline 3 (Horizontal Bottom, 3x)
 * Diagonal Down: [0,0], [1,1], [2,2] -> Payline 4 (Diagonal Down, 5x)
 * Diagonal Up: [0,2], [1,1], [2,0] -> Payline 5 (Diagonal Up, 10x)
 *
 * @param {Array<number|string|Object>|null} icons 9 icon places (0..4) or 3 center reel values
 * @param {string} category "fiat" | "crypto"
 * @param {string} order "row-major" | "reel-major"
 * @returns {Array<Array<Object>>} 3x3 matrix [ [reel0_row0, reel0_row1, reel0_row2], ... ]
 */
export const mapContractIconsToGrid = (icons, category = "fiat", order = "row-major") => {
  const resolveSymbol = (val) => {
    if (val === undefined || val === null) return getRandomSymbol(category);
    if (typeof val === "object" && val.id) return val;
    return getSymbolByContractNumber(val, category);
  };

  // If already a 2D array of 3 arrays
  if (Array.isArray(icons) && icons.length === 3 && Array.isArray(icons[0])) {
    if (order === "reel-major") {
      return [
        [resolveSymbol(icons[0][0]), resolveSymbol(icons[0][1]), resolveSymbol(icons[0][2])],
        [resolveSymbol(icons[1][0]), resolveSymbol(icons[1][1]), resolveSymbol(icons[1][2])],
        [resolveSymbol(icons[2][0]), resolveSymbol(icons[2][1]), resolveSymbol(icons[2][2])],
      ];
    }
    // row-major 2D array: icons[row][col]
    return [
      [resolveSymbol(icons[0][0]), resolveSymbol(icons[1][0]), resolveSymbol(icons[2][0])],
      [resolveSymbol(icons[0][1]), resolveSymbol(icons[1][1]), resolveSymbol(icons[2][1])],
      [resolveSymbol(icons[0][2]), resolveSymbol(icons[1][2]), resolveSymbol(icons[2][2])],
    ];
  }

  // If flat array of 9 symbols (contract standard 0..8)
  if (Array.isArray(icons) && icons.length >= 9) {
    if (order === "reel-major") {
      // Index 0..2 = Reel 0, 3..5 = Reel 1, 6..8 = Reel 2
      return [
        [resolveSymbol(icons[0]), resolveSymbol(icons[1]), resolveSymbol(icons[2])],
        [resolveSymbol(icons[3]), resolveSymbol(icons[4]), resolveSymbol(icons[5])],
        [resolveSymbol(icons[6]), resolveSymbol(icons[7]), resolveSymbol(icons[8])],
      ];
    }
    // row-major standard: 0..2 = Row 0, 3..5 = Row 1, 6..8 = Row 2
    return [
      [resolveSymbol(icons[0]), resolveSymbol(icons[3]), resolveSymbol(icons[6])], // Reel 0 [Row 0, Row 1, Row 2]
      [resolveSymbol(icons[1]), resolveSymbol(icons[4]), resolveSymbol(icons[7])], // Reel 1 [Row 0, Row 1, Row 2]
      [resolveSymbol(icons[2]), resolveSymbol(icons[5]), resolveSymbol(icons[8])], // Reel 2 [Row 0, Row 1, Row 2]
    ];
  }

  // If array of 3 symbols (center row fallback)
  if (Array.isArray(icons) && icons.length === 3) {
    return [
      [getRandomSymbol(category), resolveSymbol(icons[0]), getRandomSymbol(category)],
      [getRandomSymbol(category), resolveSymbol(icons[1]), getRandomSymbol(category)],
      [getRandomSymbol(category), resolveSymbol(icons[2]), getRandomSymbol(category)],
    ];
  }

  // Fallback: full random 3x3 grid
  return [
    [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
    [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
    [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
  ];
};

/**
 * Determine winning payout and jackpot status for 3-Reel Slot
 * Evaluates center 3 symbols (Match 3)
 */
export const calculateWinResult = (
  reels,
  betAmount = 5.0,
  isFreeSpins = false,
  jackpots = JACKPOT_DEFAULTS
) => {
  if (!reels || reels.length !== 3) {
    return { isWin: false, amount: 0, type: "none", label: "No Spin" };
  }

  const [s1, s2, s3] = reels;
  const multiplierMultiplier = isFreeSpins ? 2 : 1; // 2x boost during Free Spins

  // Check 3 of a kind exact match
  const isMatch3 =
    (s1.contractNumber !== undefined &&
      s1.contractNumber === s2.contractNumber &&
      s2.contractNumber === s3.contractNumber) ||
    (s1.id && s1.id === s2.id && s2.id === s3.id);

  if (isMatch3) {
    const contractNum = s1.contractNumber;

    // Index 0: Mini Jackpot (Golden Bell / Polygon POL)
    if (contractNum === 0 || s1.id === "bell" || s1.id === "emerald" || s1.id === "pol") {
      const winAmt =
        jackpots?.mini && jackpots.mini > 0
          ? jackpots.mini
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "mini",
        label: "MINI JACKPOT!",
        symbol: s1,
      };
    }

    // Index 1: 5 Free Spins (Casino Cherries / Solana SOL)
    if (contractNum === 1 || s1.id === "cherries" || s1.id === "sol" || s1.id === "cherry" || s1.id === "ruby") {
      const winAmt = betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 5,
        label: "5 FREE SPINS WON!",
        jackpotTier: null,
        symbol: s1,
      };
    }

    // Index 2: 10 Super Free Spins (Lucky 7 / Tether USDT)
    if (contractNum === 2 || s1.id === "lucky_7" || s1.id === "seven" || s1.id === "usdt") {
      const winAmt = betAmount * s1.multiplier * 2 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 10,
        label: "10 SUPER SPINS (2X WIN)!",
        jackpotTier: null,
        symbol: s1,
      };
    }

    // Index 3: Major Jackpot (Brilliant Diamond / Ethereum ETH)
    if (contractNum === 3 || s1.id === "diamond" || s1.id === "eth" || s1.id === "gold_nugget") {
      const winAmt =
        jackpots?.major && jackpots.major > 0
          ? jackpots.major
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "major",
        label: "MAJOR JACKPOT!",
        symbol: s1,
      };
    }

    // Index 4: Mega Jackpot (Royal Crown / Bitcoin BTC)
    if (contractNum === 4 || s1.id === "crown" || s1.id === "jackpot_crown" || s1.id === "btc") {
      const winAmt =
        jackpots?.mega && jackpots.mega > 0
          ? jackpots.mega
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "mega",
        label: "MEGA JACKPOT!",
        symbol: s1,
      };
    }

    // Fallback match 3
    const winAmt = betAmount * (s1.multiplier || 10) * multiplierMultiplier;
    return {
      isWin: true,
      amount: winAmt,
      type: "match3",
      jackpotTier: null,
      label: `3x ${s1.label.toUpperCase()}`,
      symbol: s1,
    };
  }

  // No match
  return {
    isWin: false,
    amount: 0,
    type: "loss",
    jackpotTier: null,
    label: "No Match",
  };
};

/**
 * 3-Slot Comprehensive 5-Payline Win Evaluation Engine
 * Screen: 3 spinning reels (columns) x 3 rows = 9 symbol positions
 *
 * Checks all 5 paylines against player's selected active paylines.
 * Payout Formula per Line:
 * Line Payout = Bet Per Line x Base Symbol Multiplier x Line Multiplier
 * Jackpots Rule:
 * 3 matching Bells, Diamonds, or Crowns trigger Multiplied Line Win + Full Jackpot Pool Cash!
 * Free Spins Rule:
 * 3 matching Cherries trigger 5 Free Spins; 3 matching Lucky 7s trigger 10 Super Free Spins.
 *
 * @param {Array<Array<Object>>} reelGrid 3x3 symbol matrix: reelGrid[reelIndex][rowIndex]
 * @param {Array<number>} selectedLineIds array of active line numbers (1..5)
 * @param {number} betPerLine bet amount per line (e.g. 2 BET)
 * @param {boolean} isFreeSpins whether current spin is a free spin
 * @param {Object} jackpots current pools { mini, major, mega }
 * @param {Array<Object>|null} activePaylines optional dynamic paylines list (from contract/config)
 */
export const evaluateThreeSlotSpin = (
  reelGrid,
  selectedLineIds = [1, 2, 3, 4, 5],
  betPerLine = 2.0,
  isFreeSpins = false,
  jackpots = JACKPOT_DEFAULTS,
  totalBet = null,
  activePaylines = null
) => {
  if (!reelGrid || reelGrid.length !== 3 || !reelGrid[0] || reelGrid[0].length !== 3) {
    return {
      isWin: false,
      totalWin: 0,
      winningLines: [],
      missedLines: [],
      jackpotsWon: [],
      freeSpinsWon: 0,
      label: "Invalid Grid",
    };
  }

  const winningLines = [];
  const missedLines = [];
  const jackpotsWon = [];
  let freeSpinsWon = 0;
  let totalWin = 0;

  const paylinesToEvaluate =
    Array.isArray(activePaylines) && activePaylines.length > 0
      ? activePaylines
      : THREE_SLOT_PAYLINES;

  paylinesToEvaluate.forEach((payline) => {
    const isActive = selectedLineIds.includes(payline.id);

    // Extract 3 symbols along this payline
    const [c1, c2, c3] = payline.coords;
    const s1 = reelGrid[c1[0]][c1[1]];
    const s2 = reelGrid[c2[0]][c2[1]];
    const s3 = reelGrid[c3[0]][c3[1]];

    if (!s1 || !s2 || !s3) return;

    // Check 3-of-a-kind match
    const isMatch =
      (s1.contractNumber !== undefined &&
        s1.contractNumber === s2.contractNumber &&
        s2.contractNumber === s3.contractNumber) ||
      (s1.id && s1.id === s2.id && s2.id === s3.id);

    if (isMatch) {
      const contractNum = s1.contractNumber;
      const baseMultiplier = Number(s1.multiplier) || 10;
      const lineMultiplier = Number(payline.multiplier) || 1;

      // Base Line Win = Bet Per Line x Base Symbol Multiplier x Line Multiplier
      const baseLineWin = betPerLine * baseMultiplier * lineMultiplier;

      let jackpotType = null;
      let jackpotCash = 0;
      let spinsAwarded = 0;

      // Check Mini Jackpot (Golden Bell: contractNum 0 / bell / pol)
      if (contractNum === 0 || s1.id === "bell" || s1.id === "pol" || s1.id === "emerald") {
        jackpotType = "mini";
        jackpotCash = parseFloat(jackpots?.mini) || 0;
      }
      // Check 5 Free Spins (Casino Cherries: contractNum 1 / cherries / cherry / sol)
      else if (contractNum === 1 || s1.id === "cherries" || s1.id === "cherry" || s1.id === "sol" || s1.id === "ruby") {
        spinsAwarded = 5;
      }
      // Check 10 Super Free Spins (Lucky 7: contractNum 2 / lucky_7 / seven / usdt)
      else if (contractNum === 2 || s1.id === "lucky_7" || s1.id === "seven" || s1.id === "usdt") {
        spinsAwarded = 10;
      }
      // Check Major Jackpot (Brilliant Diamond: contractNum 3 / diamond / eth)
      else if (contractNum === 3 || s1.id === "diamond" || s1.id === "eth" || s1.id === "gold_nugget") {
        jackpotType = "major";
        jackpotCash = parseFloat(jackpots?.major) || 0;
      }
      // Check Mega Jackpot (Royal Crown: contractNum 4 / crown / btc)
      else if (contractNum === 4 || s1.id === "crown" || s1.id === "btc" || s1.id === "jackpot_crown") {
        jackpotType = "mega";
        jackpotCash = parseFloat(jackpots?.mega) || 0;
      }

      const totalLineWin = baseLineWin + jackpotCash;

      const lineResult = {
        lineId: payline.id,
        lineNum: payline.lineNum,
        payline,
        symbol: s1,
        symbols: [s1, s2, s3],
        baseMultiplier,
        lineMultiplier,
        betPerLine,
        baseLineWin,
        hasJackpot: Boolean(jackpotType),
        jackpotTier: jackpotType,
        jackpotCash,
        spinsAwarded,
        totalLineWin,
        isActive,
      };

      if (isActive) {
        winningLines.push(lineResult);
        totalWin += totalLineWin;
        if (jackpotType) {
          jackpotsWon.push({ tier: jackpotType, amount: jackpotCash, lineId: payline.id });
        }
        if (spinsAwarded > 0) {
          freeSpinsWon += spinsAwarded;
        }
      } else {
        missedLines.push(lineResult);
      }
    }
  });

  const isWin = winningLines.length > 0;
  let topJackpot = null;
  if (jackpotsWon.some((j) => j.tier === "mega")) topJackpot = "mega";
  else if (jackpotsWon.some((j) => j.tier === "major")) topJackpot = "major";
  else if (jackpotsWon.some((j) => j.tier === "mini")) topJackpot = "mini";

  let label = "NO WIN";
  if (topJackpot) {
    label = `${topJackpot.toUpperCase()} JACKPOT WON!`;
  } else if (freeSpinsWon > 0 && totalWin > 0) {
    label = `WIN + ${freeSpinsWon} FREE SPINS!`;
  } else if (freeSpinsWon > 0) {
    label = `${freeSpinsWon} FREE SPINS WON!`;
  } else if (winningLines.length > 1) {
    label = `MULTI-LINE WIN (${winningLines.length} LINES)!`;
  } else if (isWin) {
    label = `WINNER (${winningLines[0]?.payline?.name}: ${winningLines[0]?.lineMultiplier}x)!`;
  }

  return {
    isWin,
    totalWin,
    amount: totalWin,
    winningLines,
    missedLines,
    jackpotsWon,
    jackpotTier: topJackpot,
    freeSpinsWon,
    type: topJackpot ? "jackpot" : freeSpinsWon > 0 ? "freespins" : isWin ? "win" : "none",
    label,
    betAmount: totalBet !== null ? totalBet : Number((betPerLine * selectedLineIds.length).toFixed(2)),
    betPerLine,
    selectedLineCount: selectedLineIds.length,
  };
};